import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Column } from 'react-table';
import { RoleService } from 'Services';
import { Icon, Popover } from '@brighterion/br-ui';
import { useGetColumnConfig } from 'Components/Table/utils';
import { RowActionDropdown } from 'Components/Table/RowActionDropdown';
import { RULE_TEST_STATUS } from 'Common/enums';

import styles from '../components/ManageRuleTestsTable/ManageRuleTestsTable.css';

const useGetManageRuleTestsColumns = (showKebabMenuCol: boolean, hideCompareCol: boolean) => {
    const { t } = useTranslation();
    const { getColConfig } = useGetColumnConfig<RuleTest>();

    const rawColumns = React.useMemo(() => {
        const regularColumns: Column<RuleTest>[] = [
            {
                Header: 'Created On',
                accessor: (d) => d?._formatted?.createdOn,
                id: '_formatted.createdOn',
                renderType: 'datetime',
                sortType: 'datetime',
                dataType: 'datetime',
                width: 185
            },
            {
                Header: 'Created By',
                accessor: (d: any) => d?._formatted?.createdBy,
                id: '_formatted.createdBy',
                dataType: 'text',
                width: 185
            },
            {
                Header: 'Data Duration',
                accessor: (d) => d?._formatted?.duration,
                id: '_formatted.duration',
                dataType: 'text',
                width: 193
            },
            {
                Header: 'Start Date',
                accessor: (d) => d?._formatted?.start,
                id: '_formatted.start',
                renderType: 'datetime',
                sortType: 'datetime',
                dataType: 'datetime',
                width: 267
            },
            {
                Header: 'End Date',
                accessor: (d) => d?._formatted?.end,
                id: '_formatted.end',
                renderType: 'datetime',
                sortType: 'datetime',
                dataType: 'datetime',
                width: 241
            },
            {
                Header: 'Status',
                accessor: (d) => d?._formatted?.status,
                id: '_formatted.status',
                renderType: 'titleCase',
                dataType: 'text',
                minWidth: 160
            }
        ];
        return regularColumns.map(getColConfig);
    }, [getColConfig]);

    const columns = React.useMemo<Column<RuleTest>[]>(() => {
        const dropdownCol: Column<RuleTest> = {
            accessor: 'id',
            id: 'take-action-on-rule-test',
            disableSortBy: true,
            width: 40,
            maxWidth: 40,
            renderType: 'overrideCell',
            Cell: ({ value: id, row }: { value: number; row: Row }) => {
                const isInProgress = row.original.status === RULE_TEST_STATUS.IN_PROGRESS;
                const menuActions: any = [
                    isInProgress
                        ? {
                              testId: 'cancel-rule-test',
                              label: t('Cancel Rule Test'),
                              handleAction: (event: React.MouseEvent) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  // TODO: integrate cancel API
                              },

                              icon: 'trash'
                          }
                        : {
                              testId: 'delete-rule-test',
                              label: t('Delete'),
                              handleAction: (event: React.MouseEvent) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  // TODO: integrate delete API
                              },

                              style: { color: 'var(--red000)' }
                          }
                ];
                return <RowActionDropdown menuId={`${id}-action-dropdown`} menuActions={menuActions} />;
            }
        };

        let cols: Column<RuleTest>[] = [];
        if (RoleService.hasRuleTestsEditAccess) {
            const testStatus = {
                [RULE_TEST_STATUS.FAILED]: 'FAILED',
                [RULE_TEST_STATUS.FINISHED]: 'FINISHED',
                [RULE_TEST_STATUS.QUEUED]: 'IN_PROGRESS',
                [RULE_TEST_STATUS.IN_PROGRESS]: 'IN_PROGRESS',
                [RULE_TEST_STATUS.CANCELLED]: 'CANCELLED',
                default: ''
            } as any;

            const iconProp = {
                FINISHED: {
                    size: 'md',
                    className: styles.statusFinishedIcon,
                    'data-testid': 'table-new'
                },
                IN_PROGRESS: {
                    size: 'md',
                    className: styles.statusTooltipIcon,
                    'data-testid': 'in-progress'
                },
                FAILED: {
                    size: 'md',
                    className: styles.statusErrorIcon,
                    'data-testid': 'exclamation-circle-solid'
                },
                CANCELLED: {
                    size: 'md',
                    className: styles.statusCancelledIcon,
                    'data-testid': 'exclamation-triangle-solid'
                }
            } as any;

            const popoverProp = {
                FAILED: {
                    content: t('Rule test could not be completed due to an error'),
                    position: 'right'
                },
                CANCELLED: {
                    content: t('Rule test was cancelled'),
                    position: 'right'
                },
                IN_PROGRESS: {
                    content: t('Rule test is in progress'),
                    position: 'right'
                },
                FINISHED: {
                    showOnHover: false
                }
            } as any;

            const statusIconCol: Column<RuleTest> = {
                accessor: 'id',
                id: 'status-id',
                disableSortBy: true,
                width: 48,
                maxWidth: 48,
                renderType: 'overrideCell',
                Cell: ({ row }: { row: Row }) => {
                    const { status = 'default' } = row.original;

                    const iconName = {
                        CANCELLED: 'exclamation-triangle-solid',
                        FAILED: 'exclamation-circle-solid',
                        IN_PROGRESS: 'in-progress',
                        FINISHED: row.original.isNewRuleTest && 'table-new'
                    } as any;

                    return (
                        <Popover
                            showOnHover
                            {...popoverProp[testStatus[status]]}
                            showArrow
                            position='top-start'
                            contentClassName={styles.popOverContentStyle}
                            arrowClassName={styles.popperArrowStyle}
                        >
                            <div>
                                <Icon name={iconName[testStatus[status]]} {...iconProp[testStatus[status]]} />
                            </div>
                        </Popover>
                    );
                }
            };

            cols = [statusIconCol, ...rawColumns];
        } else {
            cols = rawColumns;
        }

        if (showKebabMenuCol) {
            cols = cols.concat(dropdownCol);
        }
        if (hideCompareCol) {
            cols = cols.filter((col) => col.accessor !== 'compareFlag');
        }

        return cols;
    }, [rawColumns, showKebabMenuCol, hideCompareCol, t]);

    return columns;
};

export default useGetManageRuleTestsColumns;
